import { COLOR_PRIMARY } from 'src/const';
import { FinancialKPIConfig } from '../propTypes';
import {
  BAR_LESS_THAN_20_PERCENT,
  BAR_LESS_THAN_70_PERCENT,
  BAR_MORE_THAN_70_PERCENT,
} from 'src/colors';

export interface MarketMetrics {
  marketData: Record<string, FinancialKPIConfig>;
  income: Record<string, FinancialKPIConfig>;
}

export interface FinancialInputs {
  expenses: Record<string, FinancialKPIConfig>;
  initialInvestment: Record<string, FinancialKPIConfig>;
}

export interface PerformanceIndicators {
  cashflow: Record<string, FinancialKPIConfig>;
  investmentMetricts: Record<string, FinancialKPIConfig>;
}

export const marketMetrics: MarketMetrics = {
  marketData: {
    occupancyRate: {
      displayName: 'Occupancy Rate',
      key: 'ltm_occupancy_rate',
      unit: '%',
      isEditable: true,
      editMode: 'scroll',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Occupancy Rate',
        timePeriod: 'ltm',
        source: '',
        definition: [
          'The percentage of nights a typical listing is booked in this area. Calculated as booked nights divided by available nights over the past 12 months.',
        ],
        ctaText: 'Learn more',
        ctaURL: 'https://airbtics.com/glossary#occupancy-rate',
      },
    },
    nightlyRate: {
      displayName: 'Nightly Rate',
      key: 'ltm_nightly_rate',
      unit: '$',
      isEditable: true,
      editMode: 'scroll',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Nightly Rate',
        timePeriod: 'ltm',
        source: '',
        definition: [
          'The median nightly rate of booked listings in this area, excluding cleaning fees, platform fees, and extra guest fees.',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary#nightly-rate',
      },
    },
    seasonality: {
      displayName: 'Seasonality',
      key: 'seasonality',
      unit: '',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Seasonality',
        timePeriod: 'ltm',
        source: 'Airbnb Market Data (See detailed stats)',
        definition: [
          'This shows how revenue changes month to month compared to the yearly average. Calculated as the revenue difference between the highest and lowest months',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary#seaosnality',
      },
    },
    trend: {
      displayName: 'Trend',
      key: 'trend',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Trend',
        timePeriod: 'ltm',
        source: 'Airbnb Market Data (See detailed stats)',
        definition: ['This shows year-over-year changes in revenue.'],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary#trend',
      },
    },
  },
  income: {
    // revpan: {},
    revenue: {
      displayName: 'Revenue',
      key: 'ltm_revenue',
      unit: '$',
      isEditable: true,
      editMode: 'scroll',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Revenue',
        timePeriod: 'ltm',
        source: '',
        definition: [
          'The median earnings of listings in this area over the past 12 months, including both accommodation revenue and cleaning fees. This reflects the actual payout a host would receive from Airbnb.',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary#revenue',
      },
    },
  },
};

export const financialInputs: FinancialInputs = {
  expenses: {
    totalExpense: {
      displayName: 'Total Expense',
      key: 'total_expenses',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Total Expense',
        timePeriod: '',
        source: '',
        formula: 'Mortgage pay + Operating Expenses + Taxes',
        definition: ['Total Expenses includes operating expenses, taxes and mortage paymetns.'],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    netOperatingExpense: {
      displayName: 'Net Operating Expenses',
      key: 'annual_operating_expenses_total',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Net Operating Expenses',
        timePeriod: '',
        source: '',
        formula:
          'Sum of property management fees, lodging tax, maintenance, insurance, utilities (wifi, electricity, gas), tech cost, marketing cost, vacancy cost, and HOA fees',
        definition: [
          'Total amount of yearly expenses to operate a property. Typical operating expenses are property management fees, maintenance, insurance, utilities (wifi, electricity, gas), and HOA fees. ',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    mortgagePay: {
      displayName: 'Mortgage Payment',
      key: 'mortgage_annual',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Mortgage Payment',
        timePeriod: '',
        source: '',
        formula: '',
        definition: ["Total amount of mortgage payments you'll make per year."],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    taxes: {
      displayName: 'Taxes',
      key: 'taxes_total',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Taxes',
        timePeriod: '',
        source: '',
        formula: 'Property Tax + Income Tax - Depreciation',
        definition: [],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
  },
  initialInvestment: {
    totalCashInvested: {
      displayName: 'Total Cash Invested',
      key: 'total_cash_invested',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Taxes',
        timePeriod: '',
        source: '',
        formula: 'Downpayment + Startup Cost',
        definition: [],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    startupCost: {
      displayName: 'Startup Cost',
      key: 'startup_costs_total',
      unit: '$',
      isEditable: true,
      editMode: 'modal',
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Startup Cost',
        timePeriod: '',
        source: '',
        formula: 'Remodeling + Home Furnishing + Closing Costs',
        definition: [
          'Startup Cost includes all the costs you need to purchase a property and start an Airbnb, but does not include downpayment.',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
  },
};

export const performanceIndicators: PerformanceIndicators = {
  cashflow: {
    netOperatingIncome: {
      displayName: 'Net Operating Income (NOI)',
      key: 'net_operating_income',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: ['buy', 'manage', 'rent'],
      tooltipConfig: {
        name: 'Net Operating Income (NOI)',
        timePeriod: '',
        source: '',
        formula: 'Revenue - Net Operating Expenses',
        definition: [
          "Net Operating Income (NOI) shows annual cash flow before deducting mortgage payments and taxes. It shows the rental property's cashflow after deducting operating expenses.",
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    netProfitBeforeTax: {
      displayName: 'Net Profit before Tax',
      key: 'yearly_net_profit_before_tax',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage', 'rent'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Net Profit before Tax',
        timePeriod: '',
        source: '',
        formula: 'Revenue - Net Operating Expenses - Mortgage Payments',
        definition: [
          "Net Profit before Tax shows annual cash flow before deducting taxes It shows the rental property's cashflow after deducting operating expenses and mortage payments.",
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    managementFee: {
      displayName: '1 year Management Fee',
      key: 'management_fee_earned',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: null,
    },
  },
  investmentMetricts: {
    cashOnCashReturn: {
      displayName: 'Cash on Cash Return',
      key: 'cash_on_cash_return',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Cash on Cash Return',
        timePeriod: '',
        source: '',
        formula: 'Yearly Net Profit before Tax / Total Cash Invested',
        definition: [
          "Cash-on-Cash Return measures the annual cash flow of an investment property as a percentage of the total cash invested. It's often used by real estate investors to evaluate the profitability of a property. This is especially interesting for investors using financing, as they could generate relatively high cash flow compared to the amount of cash they initially invested. ",
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    capRate: {
      displayName: 'Cap Rate',
      key: 'cap_rate',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Cap Rate',
        timePeriod: '',
        source: '',
        formula: 'Net Operating Income / Current Market Value',
        definition: [
          'The cap rate (short for capitalization rate) is a simple way to measure how much money a property earns compared to its price. It’s calculated by dividing the annual profit (after expenses) by the property’s price and turning that into a percentage.',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    grossRentalYield: {
      displayName: 'Gross Rental Yield',
      key: 'gross_rental_yield',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: ['buy'],
      tooltipConfig: {
        name: 'Gross Rental Yield',
        timePeriod: '',
        source: '',
        formula: 'Yearly Revenue / Purchase Price',
        definition: [
          "Rental yield is a measure of the return on a property based on its rental revenue (before opearting expenses) relative to the property's purchase price. ",
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    netRentalYield: {
      displayName: 'Net Rental Yield',
      key: 'net_rental_yield',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: ['buy'],
      tooltipConfig: {
        name: 'Net Rental Yield',
        timePeriod: '',
        source: '',
        formula: 'Net operating Income / Purchase Price',
        definition: [
          "Rental yield is a measure of the return on a property based on its rental income (after opearting expenses) relative to the property's purchase price.",
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    dscr: {
      displayName: 'DSCR',
      key: 'dscr',
      unit: '',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'DSCR',
        timePeriod: '',
        source: '',
        formula: 'Net operating Income / Total Debt',
        definition: [
          "DSCR (Debt Service Coverage Ratio) is a financial metric used to evaluate a property's ability to generate enough income to cover its debt obligations. It’s widely used by lenders to assess the risk of financing a real estate investment.",
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    roi: {
      displayName: 'ROI',
      key: 'annual_return_rate',
      unit: '%',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: ['buy'],
      tooltipConfig: {
        name: 'ROI',
        timePeriod: '10 years average (or hold time)',
        source: '',
        formula:
          '((Total Operating Income + Sale Price – Acquisition Cost) / Acquisition cost) / Period',
        definition: [
          'ROI is the amount of value a rental property is adding per year compared to the cash you invested. This includes yearly property appreciation value and yearly net rental profit. ',
        ],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
    totalProfitWhenSold: {
      displayName: 'Total Profit when Sold',
      key: 'total_profit_when_sold',
      unit: '$',
      isEditable: false,
      editMode: null,
      allowedModes: ['buy', 'manage'],
      showAsDeafaultIn: [],
      tooltipConfig: {
        name: 'Total Profit when Sold',
        timePeriod: '10 years (or hold time)',
        source: '',
        formula: 'Total Operating Income + Sale Price – Acquisition Cost',
        definition: [],
        ctaText: 'Learn More',
        ctaURL: 'https://airbtics.com/glossary/cap-rate-calculator/',
      },
    },
  },
};

export const futureProjections = {
  tenYearsReturnRate: {
    displayName: '10 Years Return Rate',
    allowedModes: ['buy', 'manage'],
    showAsDeafaultIn: 'buy',
    key: 'ten_years_return_rate',
    unit: '%',
    legendMap: {
      value: 'Return Rate',
    },
  },
  fiveYearsCashFlow: {
    displayName: '5 Years Cash Flow',
    allowedModes: ['buy', 'manage', 'rent'],
    showAsDeafaultIn: 'manage',
    key: 'five_years_cash_flow',
    unit: '$',
    legendMap: {
      value: 'Cash Flow',
    },
  },
  twentyFourMonthsBreakevenAnalysis: {
    displayName: '24 Months Breakeven Analysis',
    allowedModes: ['rent'],
    showAsDeafaultIn: 'rent',
    key: 'breakeven_analysis_24_months',
    unit: '$',
    legendMap: {
      total_expenses: 'Total Expense',
      total_net_operating_income: 'Net Income',
    },
    barColorsMap: {
      total_expenses: BAR_LESS_THAN_70_PERCENT,
      total_net_operating_income: BAR_MORE_THAN_70_PERCENT,
    },
  },
};
